import React from "react";

interface PricingStepProps {
    rent: number | null;
    setRent: (rent: number) => void;
    bond: number | null;
    setBond: (bond: number) => void;
    billsIncluded: boolean | null;
    setBillsIncluded: (billsIncluded: boolean) => void;
    editMode?: boolean;
}

const PricingStep: React.FC<PricingStepProps> = ({
    rent,
    setRent,
    bond,
    setBond,
    billsIncluded,
    setBillsIncluded,
    editMode = false
}) => {
    const validateAndUpdateValue = (value: string, setter: (value: number) => void) => {
        if (/^\d*$/.test(value) && value.length < 6) {
            setter(value === '' ? 0 : parseInt(value, 10));
        }
    };

    const buttonClasses = (isActive: boolean) =>
        `py-3 px-4 inline-flex items-center justify-center text-base font-medium focus:z-10 ${
            isActive
                ? 'bg-gray-200 shadow-sm border-gray-200 text-[#2476f2] border'
                : 'border border-gray-200 bg-white text-gray-800 shadow-sm bg-gradient-to-br from-[#24ccf2]/0 via-[#2476f2]/0 to-purple-500/0 hover:from-[#24ccf2]/10 hover:via-[#2476f2]/10 hover:to-purple-500/10 '
        } ${isActive ? 'disabled:opacity-100' : 'disabled:opacity-50 disabled:pointer-events-none'}`;

    return (
        <div className="w-full max-w-3xl mx-auto px-4">
            {!editMode && (
                <h1 className="text-2xl font-bold text-gray-800 sm:text-4xl mb-8 text-center">
                    How much will it cost to stay here?
                </h1>
            )}
            
            <div className="space-y-6">
                {/* Rent */}
                <div className="space-y-2 flex flex-col items-center">
                    <div className="text-lg font-medium text-gray-700">
                        Rent
                    </div>
                    <div className="relative w-48">
                        <input
                            type="text"
                            value={rent?.toString() || ''}
                            onChange={(e) => validateAndUpdateValue(e.target.value, setRent)}
                            className="py-3 px-4 ps-9 w-full border border-gray-200 rounded-lg text-base focus:border-blue-500 focus:ring-blue-500"
                            placeholder="0"
                        />
                        <div className="absolute inset-y-0 start-0 flex items-center ps-4">
                            <span className="text-gray-500">$</span>
                        </div>
                    </div>
                </div>

                {/* Bond */}
                <div className="space-y-2 flex flex-col items-center">
                    <div className="text-lg font-medium text-gray-700">
                        Bond
                    </div>
                    <div className="relative w-48">
                        <input
                            type="text"
                            value={bond?.toString() || ''}
                            onChange={(e) => validateAndUpdateValue(e.target.value, setBond)}
                            className="py-3 px-4 ps-9 w-full border border-gray-200 rounded-lg text-base focus:border-blue-500 focus:ring-blue-500"
                            placeholder="0"
                        />
                        <div className="absolute inset-y-0 start-0 flex items-center ps-4">
                            <span className="text-gray-500">$</span>
                        </div>
                    </div>
                </div>

                {/* Bills Included */}
                <div className="space-y-2 flex flex-col items-center">
                    <div className="text-lg font-medium text-gray-700">
                        Bills Included?
                    </div>
                    <div className="inline-flex rounded-lg shadow-sm">
                        <button
                            type="button"
                            className={`${buttonClasses(billsIncluded === true)} rounded-s-lg w-20`}
                            onClick={() => setBillsIncluded(true)}
                        >
                            Yes
                        </button>
                        <button
                            type="button"
                            className={`${buttonClasses(billsIncluded === false)} -ms-px rounded-e-lg w-20`}
                            onClick={() => setBillsIncluded(false)}
                        >
                            No
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PricingStep;